import { useMemo, useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { startOfWeek, endOfWeek } from 'date-fns';
import { ICalendarEvent, IRBCEvent } from '../@types/calendar';

/**
 * Custom hook to fetch and transform calendar data for the scheduled posts calendar view
 *
 * @param currentDate - The date of the week to display
 * @returns Computed calendar data with loading states and events
 */
export function useScheduledPosts(currentDate: Date) {
  // Capture "now" once at mount — used to determine isPast for each post.
  // Avoids calling Date.now() during render (react-hooks/purity).
  const [nowMs] = useState(() => Date.now());

  // Calculate week boundaries
  const weekStart = useMemo(
    () => startOfWeek(currentDate, { weekStartsOn: 1 }).getTime(),
    [currentDate]
  );
  const weekEnd = useMemo(
    () => endOfWeek(currentDate, { weekStartsOn: 1 }).getTime(),
    [currentDate]
  );

  // Fetch scheduled posts for the week
  const posts = useQuery(api.socialMedia.scheduledPublishing.listScheduledPostsByWeek, {
    weekStart,
    weekEnd,
  });

  // Transform posts to calendar events
  const events = useMemo<ICalendarEvent[]>(() => {
    if (!posts) return [];

    return posts.map(
      (post: {
        _id: string;
        postId: string;
        title: string;
        scheduledFor: number;
        timezone: string;
        status: string;
        targetAccounts: ICalendarEvent['targetAccounts'];
        errorMessage?: string;
      }) => ({
        id: post._id,
        postId: post.postId,
        title: post.title,
        scheduledFor: new Date(post.scheduledFor),
        timezone: post.timezone,
        status: post.status,
        targetAccounts: post.targetAccounts,
        errorMessage: post.errorMessage,
        isDraggable: post.status === 'scheduled',
        isPast: post.scheduledFor < nowMs,
      })
    );
  }, [posts, nowMs]);

  // Transform to react-big-calendar event format
  const rbcEvents = useMemo<IRBCEvent[]>(() => {
    return events.map(event => ({
      id: event.id,
      title: event.title,
      start: event.scheduledFor,
      end: new Date(event.scheduledFor.getTime() + 60 * 60 * 1000), // 1-hour slot
      resource: event,
    }));
  }, [events]);

  // Computed values
  const hasData = events.length > 0;
  const isLoading = posts === undefined;

  return {
    posts: events,
    rbcEvents,
    hasData,
    isLoading,
    weekStart: new Date(weekStart),
    weekEnd: new Date(weekEnd),
  };
}
